import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent, Stack, Box, Typography, IconButton, LinearProgress } from '@mui/joy';
import createApi from '../api';

// Сводка на главном экране: баланс семьи (скрыт по умолчанию, как tg-spoiler в боте),
// а также траты за сегодня и за месяц — цифры, ради которых открывают приложение.
const SummaryHeader = forwardRef(({ initDataRaw }, ref) => {
    const [balance, setBalance] = useState(null);
    const [todaySpent, setTodaySpent] = useState(0);
    const [monthSpent, setMonthSpent] = useState(0);
    const [revealed, setRevealed] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        const api = createApi(initDataRaw);
        try {
            setLoading(true);
            const now = new Date();
            const [balRes, todayRes, monthRes] = await Promise.all([
                api.get('/api/balance'),
                api.get('/api/expenses/today'),
                api.get('/api/expenses/monthly', {
                    params: { month: now.getMonth() + 1, year: now.getFullYear() }
                })
            ]);

            setBalance(balRes.data.balance);

            // В «потрачено» не учитываем пополнения и служебные корректировки
            const spentToday = todayRes.data
                .filter(t => t.category !== 'ПОПОЛНЕНИЕ' && t.category !== 'КОРРЕКТИРОВКА')
                .reduce((sum, t) => sum + Number(t.amount), 0);
            setTodaySpent(spentToday);

            // /expenses/monthly уже исключает пополнения и корректировки на бэкенде
            const spentMonth = monthRes.data.reduce((sum, c) => sum + Number(c.total), 0);
            setMonthSpent(spentMonth);
        } catch (e) {
            console.error('Summary fetch error', e);
        } finally {
            setLoading(false);
        }
    }, [initDataRaw]);

    useImperativeHandle(ref, () => ({ refresh: fetchData }));

    useEffect(() => {
        if (initDataRaw) fetchData();
    }, [initDataRaw, fetchData]);

    const fmt = (v) => new Intl.NumberFormat('ru-RU').format(Math.round(v));

    return (
        <Card variant="soft" color="primary" sx={{ mb: 3 }}>
            <CardContent>
                {loading ? (
                    <LinearProgress />
                ) : (
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography level="body-sm">Баланс семьи</Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography level="h3">
                                    {revealed && balance != null ? `${fmt(balance)} ₽` : '••••• ₽'}
                                </Typography>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={() => setRevealed(v => !v)}
                                    aria-label={revealed ? 'Скрыть баланс' : 'Показать баланс'}
                                >
                                    {revealed ? '🙈' : '👁'}
                                </IconButton>
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={4}>
                            <Box>
                                <Typography level="body-xs" color="neutral">Сегодня</Typography>
                                <Typography level="title-md">{fmt(todaySpent)} ₽</Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs" color="neutral">За месяц</Typography>
                                <Typography level="title-md">{fmt(monthSpent)} ₽</Typography>
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
});

export default SummaryHeader;
